class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.watchConsent();
    this.setHeaderCartIconAccessibility();

    // CA01 free gift: keep the gift line in step with the paid total after every cart change
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.syncGift());

    // "Frequently added" row: one-tap add, then the drawer re-renders with the next suggestion
    this.addEventListener('click', (event) => {
      const upsell = event.target.closest('[data-upsell-add]');
      if (upsell) this.addUpsell(upsell);
    });
  }

  /* Shopify's accelerated-checkout component appends its subscription notice
     (#shopify-buyer-consent) some time after the wallets mount. Ease it open over its real height
     the first time it has one, so the footer grows instead of popping. */
  watchConsent() {
    const reveal = () => {
      const notice = this.querySelector('#shopify-buyer-consent:not([data-mj-shown])');
      if (!notice || !notice.offsetHeight) return;
      notice.dataset.mjShown = '';
      if (!this.isSettled || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const { height, marginTop } = getComputedStyle(notice);
      notice.style.overflow = 'hidden';
      notice
        .animate(
          [
            { height: '0px', marginTop: '0px', opacity: 0 },
            { height, marginTop, opacity: 1 },
          ],
          { duration: 350, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }
        )
        .finished.finally(() => (notice.style.overflow = ''));
    };
    new MutationObserver(reveal).observe(this, { childList: true, subtree: true, attributes: true });
  }

  addUpsell(button) {
    button.disabled = true;
    this.showError('');
    const sections = this.getSectionsToRender().map((section) => section.id);
    fetch(routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify({ items: [{ id: Number(button.dataset.upsellAdd), quantity: 1 }], sections }),
    })
      .then((response) => response.json().then((state) => ({ ok: response.ok, state })))
      .then(({ ok, state }) => {
        // /cart/add.js reports a failed add (sold out, quantity limit) as 422 {status, message, description}
        if (!ok || !state.sections) throw new Error(state.description || state.message || window.cartStrings.error);
        this.renderSections(state.sections);
        this.classList.remove('is-empty');
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', cartData: state, rendered: true });
      })
      .catch((error) => {
        button.disabled = false;
        this.showError(error.message || window.cartStrings.error);
      });
  }

  showError(message) {
    const errors = this.querySelector('#CartDrawer-CartErrors');
    if (errors) errors.textContent = message;
  }

  bindOverlay() {
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
  }

  /* CA01 free gift. Adds the gift line (hidden _mj_gift property) once the paid subtotal
     reaches the threshold and removes it when the cart drops below. The $0 price is the
     store's job — the automatic discount "Free Travel Wipe Minipack" takes $1.00 off that
     product once per order with no minimum — so the threshold is purely the theme's rule.
     "Paid subtotal" = sum of original_line_price over non-gift lines, the same figure the
     Liquid ladder uses (snippets/cart-drawer.liquid), so "unlocked" and the gift agree. */
  syncGift() {
    const { giftVariant, giftThreshold, giftAutoadd } = this.dataset;
    if (giftAutoadd !== 'true' || !giftVariant || !(Number(giftThreshold) > 0) || this.giftSyncing) return;
    this.giftSyncing = true;
    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const isGift = (item) => (item.properties && item.properties._mj_gift) || item.final_line_price === 0;
        const gift = cart.items.find(isGift);
        const paid = cart.items.reduce((sum, item) => (isGift(item) ? sum : sum + item.original_line_price), 0);
        const earned = paid >= Number(giftThreshold);
        if (earned && !gift && !this.giftUnavailable) return this.changeGift('add', giftVariant);
        if (!earned && gift) return this.changeGift('remove', gift.key);
      })
      .catch(() => {})
      .finally(() => {
        this.giftSyncing = false;
      });
  }

  changeGift(action, id) {
    // on /cart the page's lines and totals come back in the same response as the drawer's
    const pageSections = this.getPageSectionsToRender();
    const sections = [
      ...this.getSectionsToRender().map((section) => section.id),
      ...pageSections.map((section) => section.section),
    ];
    const body =
      action === 'add'
        ? { items: [{ id: Number(id), quantity: 1, properties: { _mj_gift: '1' } }], sections }
        : { id, quantity: 0, sections };
    return fetch(action === 'add' ? routes.cart_add_url : routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify(body),
    })
      .then((response) => response.json().then((state) => ({ ok: response.ok, state })))
      .then(({ ok, state }) => {
        if (!ok || !state.sections) {
          // sold out / unpublished gift: stop asking for it until the next page load
          if (action === 'add') this.giftUnavailable = true;
          return;
        }
        // the swap replaces the status region too: keep what the quantity change just announced
        // and add the tracker's new message ("You've unlocked free Travel Wipes Minipack …")
        const announced = this.querySelector('#CartDrawer-LiveRegionText')?.textContent.trim();
        return this.renderSections(state.sections).then(() => {
          this.renderPageSections(pageSections, state);
          if (typeof state.item_count === 'number') this.classList.toggle('is-empty', state.item_count === 0);
          const status = this.querySelector('#CartDrawer-LiveRegionText');
          const sentences = [announced, ...this.querySelectorAll('.ab-sidecart__tracker-text, .ab-sidecart__unlocked')]
            .map((part) => (part?.textContent ?? part ?? '').replace(/\s+/g, ' ').trim().replace(/\.$/, ''))
            .filter(Boolean);
          if (status && sentences.length) status.textContent = sentences.join('. ') + '.';
        });
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.syncGift();

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.renderSections(parsedState.sections);

    setTimeout(() => this.open());
  }

  // resolves once the DOM update has run (see transition)
  renderSections(sections) {
    return this.transition(() => {
      this.getSectionsToRender().forEach((section) => {
        if (section.id === 'cart-drawer') return this.swapInner(sections[section.id]);
        const sectionElement = document.getElementById(section.id);
        if (sectionElement) sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id]);
      });
    });
  }

  /* Every drawer re-render (quantity, plan, upsell, gift) goes through these two. */

  // Crossfades the DOM change with the View Transitions API while the drawer is open and at
  // rest; a plain swap where the API is missing, with reduced motion, when the drawer is closed,
  // or while it is still sliding in (a transition would freeze the slide mid-way).
  // Resolves once the DOM update has run (the API applies it asynchronously).
  transition(update) {
    const animate =
      this.isSettled &&
      document.startViewTransition &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!animate) return Promise.resolve(update());
    return document.startViewTransition(update).updateCallbackDone;
  }

  // open and no longer sliding in
  get isSettled() {
    return this.classList.contains('active') && this.querySelector('.drawer__inner').getAnimations().length === 0;
  }

  // Replaces .drawer__inner's markup but leaves the live wallet block (.ab-sidecart__express) in
  // place while its subscription flag is unchanged: Shopify's accelerated-checkout component
  // mounts its buttons and "One or more of the items…" notice asynchronously, so re-creating it
  // blanks the footer for a beat on every change. The block is the footer's last child and the
  // footer is the drawer's last child, so everything else is swapped around them.
  swapInner(html) {
    const inner = this.querySelector('.drawer__inner');
    const next = this.getSectionDOM(html, '.drawer__inner');
    const variantsBefore = new Set(
      [...inner.querySelectorAll('[data-quantity-variant-id]')].map((input) => input.dataset.quantityVariantId)
    );
    this.replaceInner(inner, next);
    // a product that was not in the cart before enters from the right (ab-sidecart.css)
    if (!this.isSettled) return;
    inner.querySelectorAll('.cart-item').forEach((item) => {
      const variant = item.querySelector('[data-quantity-variant-id]')?.dataset.quantityVariantId;
      if (variant && !variantsBefore.has(variant)) item.classList.add('is-entering');
    });
  }

  replaceInner(inner, next) {
    const oldExpress = inner.querySelector('.ab-sidecart__express');
    const newExpress = next.querySelector('.ab-sidecart__express');
    const oldFooter = oldExpress?.parentElement;
    const newFooter = newExpress?.parentElement;
    const keepWallets =
      oldExpress &&
      newExpress &&
      oldExpress.dataset.subscription === newExpress.dataset.subscription &&
      oldFooter.lastElementChild === oldExpress &&
      inner.lastElementChild === oldFooter;
    if (!keepWallets) {
      inner.innerHTML = next.innerHTML;
      return;
    }
    const replaceAround = (oldParent, newParent, keepOld, keepNew) => {
      [...oldParent.children].forEach((child) => child !== keepOld && child.remove());
      [...newParent.children].forEach((child) => child !== keepNew && oldParent.insertBefore(child, keepOld));
    };
    replaceAround(inner, next, oldFooter, newFooter);
    replaceAround(oldFooter, newFooter, oldExpress, newExpress);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  // The cart page's own sections (main-cart-items / main-cart-footer), when the drawer sits over
  // /cart: same ids and .js-contents swap as cart.js uses for its own quantity changes.
  getPageSectionsToRender() {
    return ['main-cart-items', 'main-cart-footer']
      .map((id) => document.getElementById(id))
      .filter((element) => element?.dataset.id)
      .map((element) => ({ id: element.id, section: element.dataset.id, selector: '.js-contents' }));
  }

  renderPageSections(pageSections, state) {
    pageSections.forEach(({ id, section, selector }) => {
      const target = document.getElementById(id);
      const next = state.sections[section] && this.getSectionDOM(state.sections[section], selector);
      if (!target || !next) return;
      (target.querySelector(selector) || target).innerHTML = next.innerHTML;
    });
    // /cart/change.js reports the new count (a removed gift can leave the cart empty); add.js does not
    if (!pageSections.length || typeof state.item_count !== 'number') return;
    const empty = state.item_count === 0;
    document.getElementById('main-cart-items')?.closest('cart-items')?.classList.toggle('is-empty', empty);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  renderSection(section, html) {
    if (section.id === 'CartDrawer') return this.closest('cart-drawer').swapInner(html);
    super.renderSection(section, html);
  }

  renderTransition(update) {
    this.closest('cart-drawer').transition(update);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
